import { defaultAllSelection } from "services/constants/selections";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BadgeOutlined,
  BusinessCenterOutlined,
  BarChartRounded,
  CalendarMonth,
  CheckCircleOutline,
  ErrorOutline,
  HourglassTop,
  ManageSearchRounded,
  Refresh,
  RemoveCircleOutline,
  VisibilityOutlined,
  WeekendOutlined,
  WorkOutline,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import {
  getThisMonthRange,
  resolvePresetLabel,
} from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useFetch } from "services/hooks/useFetch";
import {
  normalizeRangeDate,
  resolveRangeLabel,
} from "pages/TeamZone/Approve/dateRangeUtils";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
} from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { tooltipStyleProps } from "services/constants/Styles";
import { theme } from "services/constants";
import { checkViewerPermission } from "services/utils/HelperFunction";
import {
  APP_BAR_OFFSET,
  designColors,
  designFontFamily,
  designShell,
  designType,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";
import { FilterProvider } from "pages/FilterContext";

const pageShell = {
  ...designShell.pageSurface,
  px: { xs: 1.5, md: 2 },
  py: { xs: 2, md: 2.5 },
};

const eodReportHeaders = [
  {
    key: "employee_name",
    name: "Employee",
    type: "string",
    icon: <BadgeOutlined fontSize="small" />,
  },
  {
    key: "employee_code",
    name: "Employee ID",
    type: "string",
    icon: <BadgeOutlined fontSize="small" />,
  },
  {
    key: "department",
    name: "Department",
    type: "autocomplete",
    icon: <BusinessCenterOutlined fontSize="small" />,
  },
  {
    key: "report_date",
    name: "Date",
    type: "date",
    icon: <CalendarMonth fontSize="small" />,
    showBetweenOperator: true,
    dateRangeFields: {
      fromField: "report_date",
      toField: "report_date",
    },
  },
  {
    key: "worked_hours",
    name: "Hours Logged",
    type: "time",
    icon: <WorkOutline fontSize="small" />,
  },
  {
    key: "shift_requirement",
    name: "Shift Requirement",
    type: "time",
    icon: <WorkOutline fontSize="small" />,
  },
  {
    key: "compliance",
    name: "Compliance",
    type: "string",
    icon: <ManageSearchRounded fontSize="small" />,
  },
  {
    key: "status",
    name: "Status",
    type: "autocomplete",
    icon: <HourglassTop fontSize="small" />,
  },
  {
    key: "manager_name",
    name: "Reporting Manager",
    type: "string",
    icon: <BadgeOutlined fontSize="small" />,
  },
  {
    key: "report_summary",
    name: "Report Summary",
    type: "string",
    icon: <ManageSearchRounded fontSize="small" />,
  },
];

const statusToneMap = {
  Submitted: {
    bg: "#E8F0FF",
    color: "#1D67E0",
    icon: <HourglassTop sx={{ fontSize: 16 }} />,
  },
  Approved: {
    bg: "#E9F9EF",
    color: "#169A55",
    icon: <CheckCircleOutline sx={{ fontSize: 16 }} />,
  },
  Reopened: {
    bg: "#FFF3E7",
    color: "#F08A24",
    icon: <Refresh sx={{ fontSize: 16 }} />,
  },
  Missed: {
    bg: "#FFECEC",
    color: "#EF4444",
    icon: <ErrorOutline sx={{ fontSize: 16 }} />,
  },
  "Week Off": {
    bg: "#F1EAFE",
    color: "#7C3AED",
    icon: <WeekendOutlined sx={{ fontSize: 16 }} />,
  },
  Holiday: {
    bg: "#FFF0E3",
    color: "#D97706",
    icon: <WeekendOutlined sx={{ fontSize: 16 }} />,
  },
};

// The review drawer contains many consecutive labels. Keep its content at the
// shared field-label weights so it does not compete with the drawer title.
const reviewSectionTitleSx = {
  fontSize: 14,
  color: designColors.slate900,
  fontWeight: designWeights.medium,
  lineHeight: 1.35,
  mb: 1.2,
};

const reviewFieldLabelSx = {
  fontSize: 12,
  color: designColors.slate600,
  fontWeight: designWeights.regular,
};

const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const parseHoursToMinutes = (value) => {
  if (!value || typeof value !== "string" || !value.includes(":")) return 0;
  const [hours, minutes] = value.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatMinutesToHours = (minutes) => {
  const absoluteMinutes = Math.abs(minutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
  const mins = String(absoluteMinutes % 60).padStart(2, "0");
  return `${hours}:${mins}`;
};

const formatDisplayDate = (value, fallback = "N/A") => {
  if (!value) return fallback;
  const parsed = moment(value, ["DD-MM-YYYY", "DD MMM YYYY", "YYYY-MM-DD"], true);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
};

const extractDepartment = (report) =>
  report?.department_name || report?.department || report?.department_label || "General";

const extractEmployeeName = (report) =>
  report?.user_name || report?.emp_name || report?.employee_name || "Unknown Employee";

const extractEmployeeCode = (report) =>
  report?.employee_id || report?.emp_id || report?.employee_code || report?.user_code || "N/A";

const extractManagerName = (report) =>
  report?.reporting_manager ||
  report?.reporting_manager_name ||
  report?.manager_name ||
  report?.approver_name ||
  "Not assigned";

const extractStatus = (report) => {
  const status = report?.status || "Submitted";
  return status === "Weekoff" ? "Week Off" : status;
};

const extractShiftRequirement = (report) =>
  report?.shift_requirement ||
  report?.shift_required_hours ||
  report?.required_shift_hours ||
  report?.shift_hours ||
  "08:30";

const extractWorkedHours = (report) =>
  report?.total_worked_hours || report?.worked_hours || "00:00";

const extractPlannedTasks = (detail) =>
  safeArray(detail?.tasks_planned_for_today_actual || detail?.planned_tasks_today).map(
    (task, index) => ({
      id: task.id || task.task_id || `planned-${index}`,
      name: task.task_name || task.task_no || "Untitled task",
      plannedHours: task.planned_hours ? String(task.planned_hours).slice(0, 5) : "--:--",
      eta: task.remaining_eta || task.eta || "--:--",
    }),
  );

const extractCompletedTasks = (detail) =>
  safeArray(detail?.achieved_tasks || detail?.completed_tasks).map((task, index) => ({
    id: task.id || task.task_id || `done-${index}`,
    name: task.task_name || task.task_no || "Untitled task",
    workedHours: task.worked_hours || task.planned_hours || "--:--",
  }));

const extractNextTasks = (detail) =>
  safeArray(detail?.tasks_planning_for_tomorrow || detail?.next_session_tasks).map(
    (task, index) => ({
      id: task.id || task.task_id || `next-${index}`,
      name: task.task_name || task.task_no || "Untitled task",
      plannedHours: task.planned_hours ? String(task.planned_hours).slice(0, 5) : "--:--",
      eta: task.remaining_eta || task.eta || "--:--",
    }),
  );

const extractSummaryItems = (report) =>
  safeArray(report?.achieved_tasks_summary)
    .filter(Boolean)
    .map((item) => String(item));

const buildStatusChip = (status) => {
  const tone = statusToneMap[status] || statusToneMap.Submitted;
  return (
    <Chip
      icon={tone.icon}
      label={status}
      size="small"
      sx={{
        height: 28,
        bgcolor: tone.bg,
        color: tone.color,
        fontWeight: designWeights.semibold,
        borderRadius: "999px",
        "& .MuiChip-icon": { color: tone.color },
      }}
    />
  );
};

const DetailMetric = ({ label, value, helper, tone = "#0F172A" }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2.75,
      border: "1px solid #E8EDF6",
      bgcolor: "#FBFCFE",
      p: 1.5,
      minHeight: 96,
      minWidth: 0,
    }}
  >
    <Typography sx={reviewFieldLabelSx}>{label}</Typography>
    <Typography
      sx={{
        fontSize: { xs: 20, sm: 24 },
        color: tone,
        fontWeight: designWeights.semibold,
        mt: 0.75,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ fontSize: 12, color: "#64748B", mt: 0.5, overflowWrap: "anywhere" }}>
      {helper}
    </Typography>
  </Paper>
);

const DetailList = ({ title, items, renderTrailing }) => (
  <Box>
    <Typography sx={reviewSectionTitleSx}>
      {title}
    </Typography>
    <Paper
      elevation={0}
      sx={{ borderRadius: 2.5, border: "1px solid #E8EDF6", overflow: "hidden" }}
    >
      {items.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
            No items available.
          </Typography>
        </Box>
      ) : (
        items.map((item, index) => (
          <React.Fragment key={item.id}>
            <Box
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 13, color: designColors.slate900, fontWeight: designWeights.regular }}>
                {item.name}
              </Typography>
              {renderTrailing(item)}
            </Box>
            {index !== items.length - 1 && <Divider />}
          </React.Fragment>
        ))
      )}
    </Paper>
  </Box>
);

// Team Workspace Home's "Pending Daily Reports" card (PendingReportsSection
// .js's "View all"/"Go to Daily Reports") passes the date it currently has
// selected as a ?date= param (see TeamZone/Home/index.js's
// navigateToModule) — without reading it, this screen always landed on the
// same fixed "Last 7 Days" window regardless of what day Home was actually
// showing. Same shape as Approve/index.js's resolveInitialDateRange.
const resolveInitialEodDateRange = () => {
  const params = new URLSearchParams(window.location.search);
  const urlDate = moment(params.get("date") || "", "YYYY-MM-DD", true);
  if (urlDate.isValid()) {
    const iso = urlDate.format("YYYY-MM-DD");
    return { start: iso, end: iso, label: resolvePresetLabel(iso, iso) };
  }

  // Default window: This Month, 1st through today.
  const range = getThisMonthRange({ endAt: "today" });
  return { start: range.startDate, end: range.endDate, label: range.label };
};

export const TeamEODReport = ({
  subModuleId,
  setReportHeaderActions = null,
  embedded = false,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const history = useHistory();
  const location = useLocation();
  const requestedDate = new URLSearchParams(location.search).get("date");
  const { domain, userDetails, topbarCompanyName, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [teamReports, setTeamReports] = useState([]);
  const [eodReportData, setEodReportData] = useState(null);
  // Tracks which normalizedReports set eodReportData was actually computed
  // from, so a fresh date range / department swap doesn't render one frame
  // of the previous filter result (or an empty table) before CustomFilter's
  // own effect catches up — same pattern used across the other reports.
  const eodReportDataSourceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState(null);
   const [selectedReportUserId, setSelectedReportUserId] = useState(null);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [managerInputs, setManagerInputs] = useState({
    rating: 0,
    comments: "",
    reopenReason: "",
  });
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Same default window as Approval Center (Approve/index.js) — last 7 days
  // including today — unless Team Workspace Home passed a specific ?date=,
  // in which case resolveInitialEodDateRange (above) picks that up instead
  // so the very first paint already has the right range.
  const [startDate, setStartDate] = useState(
    () => resolveInitialEodDateRange().start,
  );
  const [endDate, setEndDate] = useState(
    () => resolveInitialEodDateRange().end,
  );
  const [dateRangeLabel, setDateRangeLabel] = useState(
    () => resolveInitialEodDateRange().label,
  );

  // Handles a ?date= arriving on a navigation that doesn't remount this
  // component (e.g. clicking "Pending Daily Reports" again while already
  // here) — the lazy useState initializers above only run once, on mount.
  useEffect(() => {
    if (!requestedDate || !/^\d{4}-\d{2}-\d{2}$/.test(requestedDate)) return;
    const parsed = moment(requestedDate, "YYYY-MM-DD", true);
    if (!parsed.isValid()) return;
    const iso = parsed.format("YYYY-MM-DD");
    setStartDate(iso);
    setEndDate(iso);
    setDateRangeLabel(resolvePresetLabel(iso, iso));
  }, [requestedDate]);
  // 99999 is the shared "All Departments" sentinel used across Team Zone
  // (Roster/PlanShift, Leave Summary) rather than a real department id.
  const [departmentIdValue, setDepartmentIdValue] = useState(defaultAllSelection);
  const [departmentNames, setDepartmentNames] = useState("All");
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);

  const checkAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "EOD Report",
    "EOD Report",
  );

  // Same department list source as Roster (PlanShift/index.js).
  const { result: departmentResult } = useFetch(
    domain && userDetails?.id
      ? `${domain}get-department/?user_id=${userDetails.id}&company_id=${requestCompanyId}`
      : null,
  );

  const departmentOptions = useMemo(() => {
    const list = Array.isArray(departmentResult) ? departmentResult : [];
    return [
      { id: defaultAllSelection, label: "All", department_name: "All" },
      ...list.map((department) => ({
        ...department,
        label: department.department_name,
      })),
    ];
  }, [departmentResult]);

  const updateDepartmentName = useCallback((event, data) => {
    if (!data) return;
    setDepartmentIdValue(data.id);
    setDepartmentNames(data.department_name || data.label);
    setPage(0);
  }, []);

  const handleDateRangeChange = useCallback((rangeStart, rangeEnd, rangeLabel) => {
    const normalizedStartDate = normalizeRangeDate(rangeStart);
    const normalizedEndDate = normalizeRangeDate(rangeEnd);
    if (!normalizedStartDate || !normalizedEndDate) return;
    setStartDate(normalizedStartDate);
    setEndDate(normalizedEndDate);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
    setPage(0);
  }, []);

  const fetchTeamReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customGetAPI(
        `${domain}eod-reports/?user_id=${userDetails?.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentIdValue}&company_id=${requestCompanyId}`,
      );
      if (response.ok) {
        const reports = Array.isArray(response.responseDetails)
          ? response.responseDetails
          : response.responseDetails?.results || response.responseDetails?.data || [];
        setTeamReports(reports.filter((report) => report.status !== "Draft"));
      } else {
        showToast("error", "Failed to load daily reports");
      }
    } catch (error) {
      console.error("Fetch error", error);
      showToast("error", "Failed to load daily reports");
    } finally {
      setLoading(false);
    }
  }, [domain, userDetails?.id, startDate, endDate, departmentIdValue, requestCompanyId]);

  const fetchReportDetail = useCallback(
    async (reportId) => {
      if (!reportId) {
        setSelectedReportDetail(null);
        return;
      }

      setDetailLoading(true);
      try {
        const response = await customGetAPI(`${domain}eod-reports/${reportId}/?user_id=${userDetails?.id}&selected_user_id=${selectedReportUserId}&company_id=${requestCompanyId}`);
        if (response.ok) {
          const detail = response.responseDetails || response.data || null;
          setSelectedReportDetail(detail);
          setManagerInputs((prev) => ({
            ...prev,
            rating: parseInt(detail?.ratings, 10) || 0,
            comments: detail?.approver_comments || "",
          }));
        } else {
          setSelectedReportDetail(null);
        }
      } catch (error) {
        console.error("Detail fetch error", error);
        setSelectedReportDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [domain, selectedReportUserId, userDetails?.id, requestCompanyId],
  );

  useEffect(() => {
    fetchTeamReports();
  }, [fetchTeamReports]);

  const normalizedReports = useMemo(
    () =>
      teamReports.map((report) => {
        const status = extractStatus(report);
        const workedHours = extractWorkedHours(report);
        const shiftRequirement = extractShiftRequirement(report);
        const summaryItems = extractSummaryItems(report);
        return {
          ...report,
          employee_name: extractEmployeeName(report),
          employee_code: extractEmployeeCode(report),
          department: extractDepartment(report),
          manager_name: extractManagerName(report),
          report_date: report?.report_date || "",
          worked_hours: workedHours,
          shift_requirement: shiftRequirement,
          compliance:
            status === "Week Off" || status === "Holiday"
              ? status
              : parseHoursToMinutes(workedHours) === 0
                ? "No Hours"
                : parseHoursToMinutes(workedHours) >=
                    parseHoursToMinutes(shiftRequirement)
                  ? `+${formatMinutesToHours(
                      parseHoursToMinutes(workedHours) -
                        parseHoursToMinutes(shiftRequirement),
                    )}`
                  : `-${formatMinutesToHours(
                      parseHoursToMinutes(shiftRequirement) -
                        parseHoursToMinutes(workedHours),
                    )}`,
          status,
          report_summary: summaryItems.join(" | ") || "-",
          report_summary_items: summaryItems,
        };
      }),
    [teamReports],
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_eod_saved_views",
    fullHeaders: eodReportHeaders,
    defaultHeaderKeys: [
      "employee_name",
      "department",
      "report_date",
      "worked_hours",
      "compliance",
      "status",
    ],
    screenAccess: checkAccess,
    onViewChange: handleSavedViewChange,
  });

  const eodReportDataReady = eodReportDataSourceRef.current === normalizedReports;
  const visibleRawEodReportData =
    eodReportData !== null && eodReportDataReady ? eodReportData : normalizedReports;

  const visibleEodReportData = useMemo(() => {
    if (!statusCardFilter) return visibleRawEodReportData;
    if (statusCardFilter === "Week Off / Holiday") {
      return visibleRawEodReportData.filter(
        (report) => report.status === "Week Off" || report.status === "Holiday",
      );
    }
    return visibleRawEodReportData.filter(
      (report) => report.status === statusCardFilter,
    );
  }, [visibleRawEodReportData, statusCardFilter]);

  useEffect(() => {
    if (!visibleEodReportData.length) {
      setSelectedReportId(null);
      setSelectedReportUserId(null);
      setSelectedReportDetail(null);
      return;
    }

    const visibleSelection = visibleEodReportData.find(
      (report) => report.id === selectedReportId,
    );
    const nextSelected = visibleSelection || visibleEodReportData[0];
    if (nextSelected.id !== selectedReportId) {
      setSelectedReportId(nextSelected.id);
      setSelectedReportUserId(nextSelected.user_id);
    }
  }, [selectedReportId, visibleEodReportData]);

  // selectedReportId (and its matching selectedReportUserId) get
  // auto-populated with the first visible row as soon as the list loads,
  // purely to keep a row highlighted in the table above - that's not the
  // user opening a report, so the detail endpoint must stay gated on the
  // review panel actually being open, otherwise every visit to this screen
  // fires a background detail fetch for a report nobody asked to view (and
  // surfaces its error toast if that row happens to 404 for the viewer).
  useEffect(() => {
    if (selectedReportId && isViewPanelOpen) {
      fetchReportDetail(selectedReportId);
    }
  }, [fetchReportDetail, selectedReportId, selectedReportUserId, isViewPanelOpen]);

  useEffect(() => {
    setPage(0);
  }, [startDate, endDate, departmentIdValue, statusCardFilter]);

  const summaryCounts = useMemo(() => {
    const total = visibleRawEodReportData.length;
    const countByStatus = (status) =>
      visibleRawEodReportData.filter((report) => report.status === status).length;
    const weekOffHoliday = visibleRawEodReportData.filter((report) => {
      const status = report.status;
      return status === "Week Off" || status === "Holiday";
    }).length;

    return {
      total,
      submitted: countByStatus("Submitted"),
      approved: countByStatus("Approved"),
      reopened: countByStatus("Reopened"),
      missed: countByStatus("Missed"),
      weekOffHoliday,
    };
  }, [visibleRawEodReportData]);

  const eodSummaryCards = useMemo(
    () => [
      {
        key: "All",
        title: "Total Reports",
        value: summaryCounts.total,
        icon: WorkOutline,
        accent: "#169A55",
        tint: "#EAF8EF",
      },
      {
        key: "Submitted",
        title: "Submitted",
        value: summaryCounts.submitted,
        icon: HourglassTop,
        accent: "#1D67E0",
        tint: "#E8F0FF",
      },
      {
        key: "Approved",
        title: "Approved",
        value: summaryCounts.approved,
        icon: CheckCircleOutline,
        accent: "#16A34A",
        tint: "#E9F9EF",
      },
      {
        key: "Reopened",
        title: "Reopened",
        value: summaryCounts.reopened,
        icon: Refresh,
        accent: "#F08A24",
        tint: "#FFF3E7",
      },
      {
        key: "Missed",
        title: "Missed",
        value: summaryCounts.missed,
        icon: RemoveCircleOutline,
        accent: "#EF4444",
        tint: "#FFECEC",
      },
      {
        key: "Week Off / Holiday",
        title: "Week Off / Holiday",
        value: summaryCounts.weekOffHoliday,
        icon: WeekendOutlined,
        accent: "#7C3AED",
        tint: "#F1EAFE",
      },
    ],
    [summaryCounts],
  );

  const reportMetadata = useMemo(() => {
    const base = {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentNames,
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Status Filter": statusCardFilter || "All",
    };

    eodSummaryCards.forEach((card) => {
      base[card.title] = card.value;
    });

    base["Report Generated On"] = moment().format("DD/MM/YYYY HH:mm:ss");
    return base;
  }, [
    topbarCompanyName,
    departmentNames,
    startDate,
    endDate,
    statusCardFilter,
    eodSummaryCards,
  ]);

  const handleStatusCardClick = useCallback((key) => {
    setPage(0);
    if (key === "All") {
      setStatusCardFilter("");
      return;
    }
    setStatusCardFilter((previous) => (previous === key ? "" : key));
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (nextFilteredData) => {
      eodReportDataSourceRef.current = normalizedReports;
      setEodReportData(nextFilteredData);
      if (page * rowsPerPage >= nextFilteredData.length) {
        setPage(0);
      }
    },
    [page, rowsPerPage, normalizedReports],
  );

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const headerFilterControls = useMemo(
    () => (
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleDateRangeChange}
          includeCurrentDate
          showToday
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentOptions}
          selectedValue={departmentNames}
          updateDataFunction={updateDepartmentName}
          Icons={BusinessCenterOutlined}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
      </Stack>
    ),
    [
      startDate,
      endDate,
      dateRangeLabel,
      handleDateRangeChange,
      departmentOptions,
      departmentNames,
      updateDepartmentName,
    ],
  );

  // Embedded mode (rendered inside TeamZone Reports' grouped page) now shows
  // filters inline in its own body, right under the shared page title —
  // matching Employee Attendance's row — instead of floating them beside
  // the title via setReportHeaderActions.
  useEffect(() => {
    if (!embedded || !setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [embedded, setReportHeaderActions]);

  const selectedReport = useMemo(
    () =>
      visibleEodReportData.find((report) => report.id === selectedReportId) || null,
    [selectedReportId, visibleEodReportData],
  );

  const selectedWorkedMinutes = parseHoursToMinutes(extractWorkedHours(selectedReport || {}));
  const selectedRequiredMinutes = parseHoursToMinutes(
    extractShiftRequirement(selectedReport || {}),
  );
  const selectedVarianceMinutes = selectedWorkedMinutes - selectedRequiredMinutes;

  const isSelectedSubmitted =
    (selectedReportDetail &&
      (selectedReportDetail.status === "Submitted" ||
        selectedReportDetail.is_submitted ||
        selectedReportDetail.status === "Approved" ||
        selectedReportDetail.status === "Missed")) ||
    false;

  const handleApprove = useCallback(async () => {
    if (!selectedReportId) return;
    setActionLoading(true);
    try {
      const payload = {
        approver_comments: managerInputs.comments,
        ratings: managerInputs.rating,
      };
      const res = await customAPI(
        `${domain}eod-reports/${selectedReportId}/approve/?company_id=${requestCompanyId}`,
        "PUT",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report approved.");
        setIsViewPanelOpen(false);
        fetchTeamReports();
      } else {
        showToast("error", res.message || "Approval Failed");
      }
    } catch (error) {
      console.error("Approve error", error);
      showToast("error", "Error approving report");
    } finally {
      setActionLoading(false);
    }
  }, [domain, selectedReportId, managerInputs.comments, managerInputs.rating, fetchTeamReports, requestCompanyId]);

  const handleReopen = useCallback(async () => {
    if (!selectedReportId) return;
    if (!managerInputs.reopenReason.trim()) {
      showToast("error", "Please provide a reason for reopening.");
      return;
    }
    setActionLoading(true);
    try {
      const payload = {
        reason: managerInputs.reopenReason,
        reopened_by_user_id: userDetails.id,
      };
      const res = await customAPI(
        `${domain}eod-reports/${selectedReportId}/reopen/?company_id=${requestCompanyId}`,
        "POST",
        payload,
      );
      if (res.ok) {
        showToast("success", "Report reopened.");
        setShowReopenDialog(false);
        setIsViewPanelOpen(false);
        fetchTeamReports();
      } else {
        showToast("error", res.message || "Reopen Failed");
      }
    } catch (error) {
      console.error("Reopen error", error);
      showToast("error", "Error reopening report");
    } finally {
      setActionLoading(false);
    }
  }, [domain, selectedReportId, managerInputs.reopenReason, userDetails.id, fetchTeamReports, requestCompanyId]);

  const reviewSidebarFooter = selectedReport ? (
    <Stack direction="row" spacing={1.5}>
      <SecondaryButton
        onClick={() => {
          setManagerInputs((prev) => ({ ...prev, reopenReason: "" }));
          setShowReopenDialog(true);
        }}
        disabled={actionLoading || selectedReportDetail?.status === "Reopened"}
        sx={{
          flex: 1,
          minWidth: 0,
          color: "#EF4444",
          borderColor: "#FCA5A5",
          "&:hover": {
            borderColor: "#EF4444",
            backgroundColor: "#FEF2F2",
            color: "#DC2626",
          },
        }}
      >
        Reopen
      </SecondaryButton>
      <PrimaryButton
        onClick={handleApprove}
        loading={actionLoading}
        disabled={
          !isSelectedSubmitted ||
          selectedReportDetail?.status === "Approved" ||
          selectedReportDetail?.status === "Missed"
        }
        sx={{ flex: 1, minWidth: 0 }}
      >
        Approve
      </PrimaryButton>
    </Stack>
  ) : null;

  const renderCompliance = (report) => {
    const workedMinutes = parseHoursToMinutes(extractWorkedHours(report));
    const varianceMinutes = workedMinutes - parseHoursToMinutes(extractShiftRequirement(report));
    const status = extractStatus(report);

    if (status === "Week Off" || status === "Holiday") {
      return (
        <Chip
          label={status}
          size="small"
          sx={{ bgcolor: "#F1EAFE", color: "#7C3AED", fontWeight: designWeights.semibold }}
        />
      );
    }

    const hasNoHours = workedMinutes === 0;
    const isPositive = varianceMinutes >= 0;

    return (
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flexWrap: "wrap", columnGap: 0.75, rowGap: 0.5, minWidth: 0 }}
      >
        <Chip
          label={
            hasNoHours
              ? "No Hours"
              : isPositive
                ? "Meets Shift"
                : `${formatMinutesToHours(varianceMinutes)} Short`
          }
          size="small"
          sx={{
            width: "fit-content",
            bgcolor: hasNoHours ? "#FFECEC" : isPositive ? "#E9F9EF" : "#FFF3E7",
            color: hasNoHours ? "#EF4444" : isPositive ? "#169A55" : "#F08A24",
            fontWeight: designWeights.semibold,
          }}
        />
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: hasNoHours ? "#EF4444" : isPositive ? "#169A55" : "#EF4444",
            whiteSpace: "nowrap",
          }}
        >
          {varianceMinutes >= 0 ? "+" : "-"}
          {formatMinutesToHours(varianceMinutes)}
        </Typography>
      </Stack>
    );
  };

  const renderEodReportCell = (key, report) => {
    switch (key) {
      case "employee_name":
        return (
          <Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={report?.employee_name || ""}
            disableHoverListener={!report?.employee_name}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: "#FCE7F3",
                  color: "#9D174D",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {report?.employee_name?.slice(0, 1)?.toUpperCase() || "-"}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#0F172A",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {report?.employee_name || "Unknown Employee"}
                </Typography>
              </Box>
            </Stack>
          </Tooltip>
        );
      case "department":
      case "manager_name":
      case "employee_code":
      case "worked_hours":
      case "shift_requirement":
      case "report_date":
        return report?.[key] || "N/A";
      case "status":
        return buildStatusChip(report?.status);
      case "compliance":
        return renderCompliance(report);
      case "report_summary":
        return (
          <Box component="ul" sx={{ m: 0, pl: 2.2 }}>
            {(report?.report_summary_items || []).slice(0, 2).map((item) => (
              <Typography
                key={item}
                component="li"
                sx={{ fontSize: 13, color: "#0F172A", lineHeight: 1.6 }}
              >
                {item}
              </Typography>
            ))}
            {(report?.report_summary_items || []).length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>-</Typography>
            ) : null}
          </Box>
        );
      default:
        return report?.[key] || "N/A";
    }
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider domain={domain} userDetails={userDetails} subModuleId={subModuleId}>
        {loading ? (
          <Loader />
        ) : (
          <Box sx={pageShell}>
            <Stack spacing={2.5} sx={embedded ? { pt: 1 } : undefined}>
              {!embedded ? (
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", lg: "center" }}
                  spacing={{ xs: 2, lg: 3 }}
                  sx={{ width: "100%" }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <GlobalBreadcrumb
                      sx={{ mb: 0.5 }}
                      trail={[
                        {
                          label: "Team Workspace",
                          onClick: () => history.push("/admin/teamzone/home"),
                        },
                        {
                          label: "Reports",
                          onClick: () => history.push("/admin/teamzone/report"),
                        },
                        "Daily Work / EOD Report",
                      ]}
                    />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 54,
                          height: 54,
                          borderRadius: 2,
                          background:
                            "linear-gradient(135deg, #E7FAF5 0%, #F5FBF8 100%)",
                          color: designColors.tealDark,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <BarChartRounded sx={{ fontSize: 34 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={designType.pageTitle}>
                          Daily Reports
                        </Typography>
                        <Typography sx={{ ...designType.pageSubtitle, maxWidth: 780 }}>
                          Track and manage daily reports submitted by your team members.
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: "100%", lg: "auto" },
                      display: "flex",
                      justifyContent: { xs: "flex-start", lg: "flex-end" },
                      flexShrink: 0,
                    }}
                  >
                    {headerFilterControls}
                  </Box>
                </Stack>
              ) : null}

            {/* Embedded mode's filters sit in their own row directly under
                the shared page title — same placement as Employee
                Attendance Report — instead of floating beside the title. */}
            {embedded ? (
              <Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
                {headerFilterControls}
              </Box>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(6, minmax(0, 1fr))",
                },
                "@media (max-width:599.95px)": {
                  "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
                },
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {eodSummaryCards.map((card) => (
                <StatCard
                  key={card.key}
                  icon={card.icon}
                  title={card.title}
                  value={card.value}
                  accent={card.accent}
                  tint={card.tint}
                  isActive={card.key === "All" ? !statusCardFilter : statusCardFilter === card.key}
                  onClick={() => handleStatusCardClick(card.key)}
                />
              ))}
            </Box>

            <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
              <CustomFilter
                title="Daily Reports"
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={normalizedReports}
                filteredData={visibleEodReportData}
                filteredDataReady={eodReportData !== null && eodReportDataReady}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={checkAccess}
                hideCount={true}
                isAttendanceScreenReport={true}
                labeledToolbar={true}
                showDownloadOptions={false}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
                searchPlaceholder="Search by employee, department, manager..."
              />
            </Box>

            <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
              <TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                }}
              >
                <ApproveTable stickyHeader sx={{ minWidth: 920, width: "100%" }}>
                  <TableHead sx={designShell.tableStickyHead}>
                    <TableRow sx={designShell.tableStickyHeadRow}>
                      {visibleHeaders.map((header, headerIndex) => (
                        <TableCell
                          key={header.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            ...(headerIndex === 0 ? { borderTopLeftRadius: 0 } : {}),
                            ...(headerIndex === visibleHeaders.length - 1
                              ? { borderTopRightRadius: 0 }
                              : {}),
                          }}
                        >
                          {header.name}
                        </TableCell>
                      ))}
                      <TableCell
                        sx={{
                          ...approveTableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          width: 88,
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                      {visibleEodReportData?.length === 0 ? (
                        <TableEmptyState
                          dataLength={normalizedReports}
                          colSpan={visibleHeaders.length + 1}
                        />
                      ) : (
                        visibleEodReportData
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((report) => {
                            const isSelected = report.id === selectedReportId;
                            const reportStatus = extractStatus(report);
                            const isNonWorking =
                              reportStatus === "Week Off" || reportStatus === "Holiday";
                            const openReportPanel = () => {
                              if (isNonWorking) return;
                              setSelectedReportId(report.id);
                              setSelectedReportUserId(report.user_id);
                              setIsViewPanelOpen(true);
                            };
                            return (
                              <TableRow
                                key={report.id}
                                hover={!isNonWorking}
                                onClick={openReportPanel}
                                sx={{
                                  cursor: isNonWorking ? "default" : "pointer",
                                  ...designShell.tableRowHover,
                                  ...(isSelected ? designShell.neutralSelectedRow : {}),
                                  "& td": {
                                    backgroundColor: isNonWorking ? "#F5F6F8" : designColors.white,
                                    color: isNonWorking ? "#9AA3B2" : "inherit",
                                    verticalAlign: "top",
                                  },
                                  "&:last-child td": { borderBottom: "none" },
                                }}
                              >
                                {visibleHeaders.map((header) => (
                                  <TableCell key={header.key} sx={approveTableBodyCell}>
                                    {renderEodReportCell(header.key, report)}
                                  </TableCell>
                                ))}
                                <TableCell align="right" sx={approveTableBodyCell}>
                                  {isNonWorking ? null : (
                                    <Tooltip title="View">
                                      <IconButton
                                        size="small"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openReportPanel();
                                        }}
                                      >
                                        <VisibilityOutlined
                                          sx={{ color: designColors.tealDark, fontSize: 20 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                  </TableBody>
                </ApproveTable>
              </TableContainer>

              <Box ref={footerRef} sx={approveStickyFooterBar}>
                {paginationFooter({
                  page,
                  rowsPerPage,
                  total: visibleEodReportData?.length || 0,
                  itemLabel: "daily reports",
                  onPageChange: setPage,
                  onRowsPerPageChange: handleChangeRowsPerPage,
                  rowsPerPageOptions,
                })}
              </Box>
            </Box>
          </Stack>

          <GlobalSidePanel
            open={isViewPanelOpen}
            onClose={() => setIsViewPanelOpen(false)}
            title="Daily Report Review"
            subtitle="Review and take action on the employee's report."
            headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
            breadcrumb={[
              {
                label: "Team Workspace",
                onClick: () => history.push("/admin/teamzone/home"),
              },
              {
                label: "Reports",
                onClick: () => history.push("/admin/teamzone/report"),
              },
              selectedReport
                ? extractEmployeeName(selectedReport)
                : "Daily Report Review",
            ]}
            footer={reviewSidebarFooter}
            bodySx={{
              backgroundColor: designColors.pageBg,
              fontFamily: designFontFamily,
              "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-input, & .MuiTableCell-root": {
                fontFamily: designFontFamily,
              },
            }}
            PaperProps={{
              sx: {
                backgroundColor: designColors.white,
                maxWidth: "92vw",
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                overflow: "hidden",
              },
            }}
          >
            {!selectedReport ? (
              <Box sx={{ py: 8, px: 3, textAlign: "center" }}>
                <Typography sx={{ fontSize: 14, color: "#64748B" }}>
                  Select a report to preview its details.
                </Typography>
              </Box>
            ) : detailLoading ? (
              <Loader />
            ) : (
              <Stack spacing={2} sx={{ p: 2.25 }}>
                <RequestDetailHeader
                  userDetails={{
                    image_url: selectedReport?.image_url,
                    employee_id: extractEmployeeCode(selectedReport),
                    user_name: extractEmployeeName(selectedReport),
                    department_name: extractDepartment(selectedReport),
                    designation: selectedReport?.designation,
                  }}
                  status={extractStatus(selectedReport)}
                  colorCode={statusToneMap[extractStatus(selectedReport)]?.color}
                />

                <Box sx={detailCardSx}>
                  <Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
                    Report Details
                  </Typography>
                  <RequestDetailField
                    icon={CalendarMonth}
                    label="Date"
                    value={formatDisplayDate(selectedReport.report_date, "-")}
                    accent={detailAccentPalette.indigo}
                  />
                  <RequestDetailField
                    icon={WorkOutline}
                    label="Shift Requirement"
                    value={`${extractShiftRequirement(selectedReport)} Shift`}
                    accent={detailAccentPalette.blue}
                  />
                  <RequestDetailField
                    icon={BadgeOutlined}
                    label="Reporting Manager"
                    value={extractManagerName(selectedReport)}
                    accent={detailAccentPalette.green}
                    isLast
                  />
                </Box>

                <Box>
                  <Typography sx={reviewSectionTitleSx}>
                    Work Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.25,
                    }}
                  >
                    <DetailMetric
                      label="Worked Hours"
                      value={extractWorkedHours(selectedReport)}
                      helper={`Shift requirement ${extractShiftRequirement(selectedReport)}`}
                    />
                    <DetailMetric
                      label="Compliance Status"
                      value={
                        extractStatus(selectedReport) === "Week Off" ||
                        extractStatus(selectedReport) === "Holiday"
                          ? extractStatus(selectedReport)
                          : selectedVarianceMinutes >= 0
                            ? "On Track"
                            : "Below Hours"
                      }
                      helper={`Variance ${selectedVarianceMinutes >= 0 ? "+" : "-"}${formatMinutesToHours(selectedVarianceMinutes)}`}
                      tone={
                        extractStatus(selectedReport) === "Week Off" ||
                        extractStatus(selectedReport) === "Holiday"
                          ? "#7C3AED"
                          : selectedVarianceMinutes >= 0
                            ? "#169A55"
                            : "#F97316"
                      }
                    />
                  </Box>
                </Box>

                <DetailList
                  title="Planned Tasks"
                  items={extractPlannedTasks(selectedReportDetail)}
                  renderTrailing={(item) => (
                    <Typography sx={{ ...reviewFieldLabelSx, color: "#475467" }}>
                      {item.plannedHours}
                    </Typography>
                  )}
                />

                <DetailList
                  title="Completed Tasks"
                  items={extractCompletedTasks(selectedReportDetail)}
                  renderTrailing={(item) => (
                    <Typography sx={{ ...reviewFieldLabelSx, color: "#475467" }}>
                      {item.workedHours}
                    </Typography>
                  )}
                />

                <DetailList
                  title="Planned for Next Session"
                  items={extractNextTasks(selectedReportDetail)}
                  renderTrailing={(item) => (
                    <Typography sx={{ ...reviewFieldLabelSx, color: "#475467" }}>
                      {item.eta}
                    </Typography>
                  )}
                />

                <Box>
                  <Typography sx={reviewSectionTitleSx}>
                    Blockers / Notes
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: "1px solid #E8EDF6",
                      bgcolor: "#F5FFF8",
                      p: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#166534" }}>
                      {selectedReportDetail?.blockers_notes ||
                        selectedReport?.blockers_notes ||
                        "No blockers or notes reported."}
                    </Typography>
                  </Paper>
                </Box>

                <Box>
                  <Typography sx={reviewSectionTitleSx}>
                    Manager Review
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{ borderRadius: 2.5, border: "1px solid #E8EDF6", p: 1.5 }}
                  >
                    <Typography sx={reviewFieldLabelSx}>
                      Rating
                    </Typography>
                    <Rating
                      value={managerInputs.rating}
                      onChange={(event, value) =>
                        setManagerInputs((prev) => ({ ...prev, rating: value || 0 }))
                      }
                      disabled={!isSelectedSubmitted}
                      sx={{ mt: 0.5 }}
                    />
                    <Typography sx={{ ...reviewFieldLabelSx, mt: 1.5 }}>
                      Comments
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={managerInputs.comments}
                      onChange={(event) =>
                        setManagerInputs((prev) => ({ ...prev, comments: event.target.value }))
                      }
                      placeholder="Add your comments here..."
                      disabled={!isSelectedSubmitted || selectedReportDetail?.status === "Approved"}
                      sx={{ mt: 0.75 }}
                    />
                  </Paper>
                </Box>
              </Stack>
            )}
          </GlobalSidePanel>

          <GlobalConfirmDialog
            open={showReopenDialog}
            title="Reopen Report"
            confirmLabel="Reopen"
            cancelLabel="Cancel"
            onCancel={() => setShowReopenDialog(false)}
            onConfirm={handleReopen}
            loading={actionLoading}
            confirmDisabled={!managerInputs.reopenReason.trim()}
            maxWidth="sm"
          >
            <Typography sx={{ fontSize: 14.5, color: designColors.slate700, mb: 1.5 }}>
              Please provide a reason for reopening this report:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Explain why this report is being reopened..."
              value={managerInputs.reopenReason}
              onChange={(event) =>
                setManagerInputs((prev) => ({ ...prev, reopenReason: event.target.value }))
              }
              sx={{ bgcolor: designColors.white }}
            />
          </GlobalConfirmDialog>
          </Box>
        )}
      </FilterProvider>
    </ThemeProvider>
  );
};

export const TeamEODReports = () => {
  const { userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const eodModule = userScreenAccess?.module_details?.find(
    (moduleDetail) => moduleDetail.module_name === "EOD Report",
  );
  const subModuleId = eodModule?.submodules?.find(
    (subModule) => subModule.sub_module_name === "EOD Report",
  )?.sub_module_id;

  return <TeamEODReport subModuleId={subModuleId} />;
};

export default TeamEODReports;
